import type { CSSObject } from '@emotion/react';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { IconRefreshSmall } from '@amzn/stencil-react-icons';

interface RefreshButtonProps {
  /**
   * Invoked when the user taps the button. Callers own the actual
   * refetch (typically `queryClient.invalidateQueries({ queryKey: [...] })`)
   * so this component stays feature-agnostic — the same button drives
   * task-list, audit-list, and any future paginated surface.
   */
  onRefresh: () => void;
  /**
   * True while the underlying data source is refetching. Disables the
   * button (so a second tap can't fire while the first is in-flight —
   * see the async-interleaving guideline) and rotates the icon.
   */
  isRefreshing: boolean;
  ariaLabel: string;
  /** Stable Stencil test ID for integration-test state assertions. */
  dataTestId?: string;
}

/**
 * Emotion CSS for the spinning icon. Defined at module scope so the
 * reference is stable across renders and so the spread pattern below
 * doesn't allocate a fresh object on every render — cheap protection
 * against downstream memoization tearing on the icon's props.
 */
const SPIN_STYLES: CSSObject = {
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  'animation': 'spin 0.8s linear infinite',
};

/**
 * Tertiary icon-only button that triggers a data refresh. Rotates its
 * icon while the caller reports `isRefreshing`, and stays disabled
 * during that window so a rapid double-tap can't queue a redundant
 * refetch on top of the one already in flight.
 *
 * Lives in `shared/ui/` from the start: task-list uses it now, the
 * audit-list is queued behind AL-06 in the parity checklist. The
 * spinner comes from a scoped `@keyframes` animation on the icon
 * itself (mirrors the pattern in `GBSAVibeCodeTemplate/settings.tsx`)
 * — no Spinner overlay, so layout stays stable while refreshing.
 *
 * The `css` prop is spread conditionally rather than passed as
 * `css={... | undefined}` because Stencil's icon prop signature is
 * strict about optional keys under `exactOptionalPropertyTypes` — an
 * explicit `undefined` is rejected at compile time.
 */
export function RefreshButton({
  onRefresh,
  isRefreshing,
  ariaLabel,
  dataTestId,
}: RefreshButtonProps): JSX.Element {
  return (
    <Button
      variant={ButtonVariant.Tertiary}
      icon={
        <IconRefreshSmall
          aria-hidden="true"
          {...(isRefreshing ? { css: SPIN_STYLES } : {})}
        />
      }
      onClick={onRefresh}
      disabled={isRefreshing}
      aria-label={ariaLabel}
      {...(dataTestId ? { dataTestId } : {})}
    />
  );
}
